/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useCurrentUser } from "context/UserContext";

const API_BASE = protectedResources.apiTodoList.endpoint;

// ID114 — reduce a URL/bare input to a hostname and validate it's a real domain (rejects "test").
const DOMAIN_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/i;
function parseDomain(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  try {
    const url = s.includes("://") ? s : `https://${s}`;
    return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return s
      .split("/")[0]
      .replace(/^www\./, "")
      .toLowerCase();
  }
}
function isValidDomainStr(domain) {
  return String(domain || "").length <= 253 && DOMAIN_RE.test(String(domain || ""));
}

// ID146 — MBFC "Factual Reporting" scale (very_high…very_low), best → worst.
const FACTUAL_META = {
  very_high: { rank: 6, label: "Very High", color: "#1b5e20", bg: "#c8e6c9" },
  high: { rank: 5, label: "High", color: "#2e7d32", bg: "#e8f5e9" },
  mostly_factual: { rank: 4, label: "Mostly Factual", color: "#f57f17", bg: "#fff8e1" },
  mixed: { rank: 3, label: "Mixed", color: "#e65100", bg: "#fff3e0" },
  low: { rank: 2, label: "Low", color: "#d84315", bg: "#fbe9e7" },
  very_low: { rank: 1, label: "Very Low", color: "#b71c1c", bg: "#ffebee" },
};
const FACTUAL_ORDER = ["very_high", "high", "mostly_factual", "mixed", "low", "very_low"];
const FACTUAL_OPTIONS = FACTUAL_ORDER.map((k) => ({ value: k, label: FACTUAL_META[k].label }));

function FactualBadge({ level, size = "small" }) {
  const meta = FACTUAL_META[level];
  if (!meta) {
    return (
      <Chip
        label="Unranked"
        size={size}
        sx={{
          bgcolor: "#f5f5f5",
          color: "#757575",
          fontWeight: 600,
          fontSize: size === "small" ? 11 : 13,
        }}
      />
    );
  }
  return (
    <Chip
      label={meta.label}
      size={size}
      sx={{
        bgcolor: meta.bg,
        color: meta.color,
        fontWeight: 700,
        fontSize: size === "small" ? 11 : 13,
        border: `1px solid ${meta.color}33`,
      }}
    />
  );
}

export default function HorizonPriorityDomains() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const { role } = useCurrentUser();
  const isAdmin = ["RISK_ADMIN", "SUPER_RISK_ADMIN"].includes((role || "").toUpperCase());

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [msg, setMsg] = useState({ text: "", severity: "info" });
  const [levelFilter, setLevelFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Server-side pagination (the dataset can be thousands of rows).
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0 });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addUrl, setAddUrl] = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addLevel, setAddLevel] = useState("high");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(""); // validation/save error shown INSIDE the Add dialog

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editLevel, setEditLevel] = useState("high");
  const [editBusy, setEditBusy] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // ID148 — block sources into the Ignored Domains list.
  const [ignoredSet, setIgnoredSet] = useState(new Set()); // domains already ignored (lowercase)
  const [selected, setSelected] = useState(new Set()); // domains ticked on the current page
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockConfirm, setBlockConfirm] = useState(null); // { mode:'selected'|'level', level?, domains?, count, label }

  // Load the current ignore list so we can mark already-blocked rows (admins only).
  const fetchIgnored = useCallback(async () => {
    try {
      const res = await executeRef.current(
        "GET",
        `${API_BASE}/anticipateHorizonScan/ignore-domains/list`
      );
      const list = Array.isArray(res?.data) ? res.data : [];
      setIgnoredSet(new Set(list.map((d) => String(d.domain || "").toLowerCase())));
    } catch {
      /* non-fatal — the page still works without the blocked indicator */
    }
  }, []);

  const fetchSources = useCallback(async (p, limit, searchTerm, level) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (searchTerm) params.set("search", searchTerm);
      if (level && level !== "all") params.set("level", level);
      const res = await executeRef.current(
        "GET",
        `${API_BASE}/source-reliability/list?${params.toString()}`
      );
      const data = res?.data || {};
      setSources(Array.isArray(data.items) ? data.items : []);
      setTotal(Number.isFinite(data.total) ? data.total : 0);
      setCounts(data.counts || { all: 0 });
    } catch {
      setMsg({ text: "Failed to load sources.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce the search box so we don't fetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  // Reset to the first page whenever the filter/search changes.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, levelFilter]);

  // Fetch the current page whenever paging/filter/search changes.
  useEffect(() => {
    fetchSources(page, rowsPerPage, debouncedSearch, levelFilter);
  }, [fetchSources, page, rowsPerPage, debouncedSearch, levelFilter]);

  const reload = useCallback(() => {
    fetchSources(page, rowsPerPage, debouncedSearch, levelFilter);
  }, [fetchSources, page, rowsPerPage, debouncedSearch, levelFilter]);

  // ID148 — load the ignore list once for admins (drives the "Blocked" indicator).
  useEffect(() => {
    if (isAdmin) fetchIgnored();
  }, [isAdmin, fetchIgnored]);

  // Clear ticks whenever the visible page/filter/search changes (selection is per-view).
  useEffect(() => {
    setSelected(new Set());
  }, [page, rowsPerPage, debouncedSearch, levelFilter]);

  const handleSeed = async () => {
    setSeeding(true);
    setMsg({ text: "", severity: "info" });
    try {
      const res = await executeRef.current("POST", `${API_BASE}/source-reliability/seed`, {});
      const d = res?.data || {};
      setMsg({
        text: `Loaded ${d.total ?? 0} Media Bias / Fact Check sources (${d.inserted ?? 0} new, ${
          d.updated ?? 0
        } updated, ${d.manualKept ?? 0} manual kept).`,
        severity: "success",
      });
      reload();
    } catch {
      setMsg({ text: "Load failed.", severity: "error" });
    } finally {
      setSeeding(false);
    }
  };

  const handleAdd = async () => {
    if (!addUrl.trim()) return;
    // ID114 — must be a valid domain before saving. Show the error INSIDE the dialog.
    const dom = parseDomain(addUrl);
    if (!isValidDomainStr(dom)) {
      setAddError(
        `"${addUrl.trim()}" is not a valid domain. Enter a real domain like "example.com".`
      );
      return;
    }
    setAddBusy(true);
    setAddError("");
    try {
      const res = await executeRef.current("POST", `${API_BASE}/source-reliability/create`, {
        url: addUrl.trim(),
        title: addTitle.trim() || undefined,
        factualReporting: addLevel,
      });
      if (res?.success) {
        setMsg({
          text: `"${addTitle || addUrl}" added as ${FACTUAL_META[addLevel].label}.`,
          severity: "success",
        });
        setAddOpen(false);
        setAddUrl("");
        setAddTitle("");
        setAddLevel("high");
        setAddError("");
        reload();
      } else {
        setAddError(res?.message || "Failed to add source.");
      }
    } catch {
      setAddError("Failed to add source.");
    } finally {
      setAddBusy(false);
    }
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditBusy(true);
    try {
      const res = await executeRef.current(
        "PUT",
        `${API_BASE}/source-reliability/update/${editTarget.id}`,
        {
          title: editTitle.trim(),
          factualReporting: editLevel,
        }
      );
      if (res?.success) {
        setMsg({ text: "Source updated.", severity: "success" });
        setEditTarget(null);
        reload();
      } else {
        setMsg({ text: res?.message || "Update failed.", severity: "error" });
      }
    } catch {
      setMsg({ text: "Update failed.", severity: "error" });
    } finally {
      setEditBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await executeRef.current(
        "DELETE",
        `${API_BASE}/source-reliability/delete/${deleteTarget.id}`
      );
      setMsg({
        text: `"${deleteTarget.title || deleteTarget.domain}" removed.`,
        severity: "success",
      });
      setDeleteTarget(null);
      reload();
    } catch {
      setMsg({ text: "Delete failed.", severity: "error" });
    } finally {
      setDeleteBusy(false);
    }
  };

  // ID148 — run the bulk block (either an explicit domain list, or a whole factual level).
  const runBlock = async () => {
    if (!blockConfirm) return;
    setBlockBusy(true);
    const payload =
      blockConfirm.mode === "level"
        ? { level: blockConfirm.level }
        : { domains: blockConfirm.domains };
    try {
      const res = await executeRef.current(
        "POST",
        `${API_BASE}/anticipateHorizonScan/ignore-domains/add-bulk`,
        payload
      );
      if (res?.success) {
        const { added = 0, skipped = 0, invalid = 0 } = res.data || {};
        setMsg({
          text: `Blocked ${added} domain(s)${skipped ? `, ${skipped} already blocked` : ""}${
            invalid ? `, ${invalid} skipped (invalid)` : ""
          }. Future ingest will skip them; existing results are unchanged.`,
          severity: added > 0 ? "success" : "info",
        });
        setSelected(new Set());
        fetchIgnored();
      } else {
        setMsg({ text: res?.message || "Failed to block domains.", severity: "error" });
      }
    } catch {
      setMsg({ text: "Failed to block domains.", severity: "error" });
    } finally {
      setBlockBusy(false);
      setBlockConfirm(null);
    }
  };

  // Rows + counts now come from the server (paginated). `sources` is already the current page.
  const filtered = sources;
  const levelCounts = counts;

  // ID148 — selection state for the checkbox column (already-ignored rows aren't selectable).
  const isIgnored = (s) => ignoredSet.has(String(s.domain || "").toLowerCase());
  const selectableDomains = filtered.filter((s) => s.domain && !isIgnored(s)).map((s) => s.domain);
  const allPageSelected =
    selectableDomains.length > 0 && selectableDomains.every((d) => selected.has(d));
  const somePageSelected = selectableDomains.some((d) => selected.has(d));
  const toggleRow = (domain) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) selectableDomains.forEach((d) => next.delete(d));
      else selectableDomains.forEach((d) => next.add(d));
      return next;
    });
  // Show "Block all <Level>" when a single factual level (or Unranked) is filtered.
  const levelBlockable = levelFilter !== "all";
  const levelLabel =
    levelFilter === "unranked" ? "Unranked" : FACTUAL_META[levelFilter]?.label || levelFilter;
  const levelCount = levelCounts[levelFilter] || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <MDTypography variant="h5" fontWeight="bold">
              Horizon Scan - Trusted Sources
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ mt: 0.5, maxWidth: 680 }}>
              Scanning results are ranked by the Media Bias / Fact Check &quot;Factual
              Reporting&quot; scale (Very High → Very Low). Sources not in the database are noted as
              &apos;Unranked&apos;. You can add sources or override the default rating below.
            </MDTypography>
          </Box>
          {isAdmin && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <MDButton
                variant="outlined"
                disabled={seeding}
                onClick={handleSeed}
                sx={{ borderColor: "#52009b", color: "#52009b", textTransform: "none" }}
              >
                {seeding ? "Loading…" : "Load Media Bias ratings"}
              </MDButton>
              <MDButton
                variant="contained"
                onClick={() => {
                  setAddError("");
                  setAddOpen(true);
                }}
                sx={{
                  bgcolor: "#52009b",
                  color: "#f7f4fb",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#3d0075" },
                }}
              >
                + Add source
              </MDButton>
            </Box>
          )}
        </Box>

        {/* Factual Reporting legend + filter chips */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip
            label={`All (${levelCounts.all || 0})`}
            onClick={() => setLevelFilter("all")}
            variant={levelFilter === "all" ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              bgcolor: levelFilter === "all" ? "#52009b" : undefined,
              color: levelFilter === "all" ? "#fff" : undefined,
            }}
          />
          {FACTUAL_ORDER.map((k) => {
            const meta = FACTUAL_META[k];
            const count = levelCounts[k] || 0;
            return (
              <Chip
                key={k}
                label={`${meta.label} (${count})`}
                onClick={() => setLevelFilter(levelFilter === k ? "all" : k)}
                variant={levelFilter === k ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  bgcolor: levelFilter === k ? meta.color : meta.bg,
                  color: levelFilter === k ? "#fff" : meta.color,
                  borderColor: meta.color,
                }}
              />
            );
          })}
          <Chip
            label={`Unranked (${levelCounts.unranked || 0})`}
            onClick={() => setLevelFilter(levelFilter === "unranked" ? "all" : "unranked")}
            variant={levelFilter === "unranked" ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              fontSize: 11,
              bgcolor: levelFilter === "unranked" ? "#757575" : "#f5f5f5",
              color: levelFilter === "unranked" ? "#fff" : "#757575",
              borderColor: "#bdbdbd",
            }}
          />
        </Box>

        {msg.text && (
          <Alert
            severity={msg.severity}
            onClose={() => setMsg({ text: "", severity: "info" })}
            sx={{ mb: 2 }}
          >
            {msg.text}
          </Alert>
        )}

        {/* Scale explanation */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          {FACTUAL_ORDER.map((k) => (
            <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: FACTUAL_META[k].color }}
              />
              <Typography variant="caption" sx={{ color: FACTUAL_META[k].color, fontWeight: 600 }}>
                {FACTUAL_META[k].label}
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#9e9e9e" }} />
            <Typography variant="caption" color="text.secondary">
              Unranked (not in database)
            </Typography>
          </Box>
        </Box>

        <TextField
          size="small"
          placeholder="Search by name or domain…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5, width: 320 }}
        />

        {/* ID148 — block sources into the Ignored Domains list (RISK_ADMIN only) */}
        {isAdmin && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 1.5,
              p: selected.size > 0 ? 1 : 0,
              px: selected.size > 0 ? 1.5 : 0,
              borderRadius: 1,
              bgcolor: selected.size > 0 ? "#fdecea" : "transparent",
              border: selected.size > 0 ? "1px solid #f5c6c0" : "none",
            }}
          >
            {selected.size > 0 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#b71c1c" }}>
                  {selected.size} selected
                </Typography>
                <MDButton
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={blockBusy}
                  onClick={() =>
                    setBlockConfirm({
                      mode: "selected",
                      domains: [...selected],
                      count: selected.size,
                      label: `${selected.size} selected source${selected.size === 1 ? "" : "s"}`,
                    })
                  }
                >
                  Block selected → Ignored Domains
                </MDButton>
                <MDButton size="small" variant="text" onClick={() => setSelected(new Set())}>
                  Clear
                </MDButton>
              </>
            )}
            {levelBlockable && levelCount > 0 && (
              <Tooltip
                title={`Block every "${levelLabel}" source (${levelCount}) — including those on other pages.`}
              >
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={blockBusy}
                  onClick={() =>
                    setBlockConfirm({
                      mode: "level",
                      level: levelFilter,
                      count: levelCount,
                      label: `all "${levelLabel}" sources (${levelCount})`,
                    })
                  }
                  sx={{ textTransform: "none" }}
                >
                  Block all &quot;{levelLabel}&quot; ({levelCount})
                </MDButton>
              </Tooltip>
            )}
          </Box>
        )}

        {loading && filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : (
          <>
            <Table size="small" sx={{ "& td, & th": { py: "6px" } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  {isAdmin && (
                    <TableCell padding="checkbox">
                      <Tooltip title="Select all on this page (excludes already-blocked)">
                        <span>
                          <Checkbox
                            size="small"
                            disabled={selectableDomains.length === 0}
                            checked={allPageSelected}
                            indeterminate={!allPageSelected && somePageSelected}
                            onChange={toggleAllPage}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Domain</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>
                    Factual Reporting
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Bias</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#52009b" }}>Type</TableCell>
                  {isAdmin && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 5}>
                      <Typography variant="body2" color="text.secondary">
                        {counts.all === 0 && !debouncedSearch && levelFilter === "all"
                          ? 'No sources yet. Click "Load Media Bias ratings" to seed the list, or add sources manually.'
                          : "No sources match the current filter."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((s) => {
                  const blocked = isIgnored(s);
                  return (
                    <TableRow
                      key={s.id}
                      hover
                      selected={selected.has(s.domain)}
                      sx={blocked ? { opacity: 0.55 } : undefined}
                    >
                      {isAdmin && (
                        <TableCell padding="checkbox">
                          {blocked ? (
                            <Tooltip title="Already in Ignored Domains — ingest skips it">
                              <Chip
                                label="Blocked"
                                size="small"
                                sx={{
                                  bgcolor: "#fdecea",
                                  color: "#b71c1c",
                                  fontWeight: 700,
                                  fontSize: 10,
                                  height: 20,
                                  border: "1px solid #f5c6c0",
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Checkbox
                              size="small"
                              checked={selected.has(s.domain)}
                              onChange={() => toggleRow(s.domain)}
                              disabled={!s.domain}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {s.title || s.domain}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {s.domain}
                          </Typography>
                          <Tooltip title={s.mbfcUrl ? "Open MBFC profile" : "Open website"}>
                            <IconButton
                              size="small"
                              component="a"
                              href={s.mbfcUrl || `https://${s.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon sx={{ fontSize: "0.8rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FactualBadge level={s.factualReporting} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {s.bias || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {s.source === "manual" ? "Manual (override)" : "Media Bias / Fact Check"}
                        </Typography>
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Edit rating">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditTarget(s);
                                setEditTitle(s.title || "");
                                setEditLevel(s.factualReporting || "high");
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(s)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </>
        )}

        {/* Add dialog */}
        <Dialog
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            setAddError("");
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Add source</DialogTitle>
          <DialogContent
            sx={{ pt: "16px !important", display: "flex", flexDirection: "column", gap: 2 }}
          >
            {addError && (
              <Alert severity="error" onClose={() => setAddError("")}>
                {addError}
              </Alert>
            )}
            <TextField
              size="small"
              label="Website URL *"
              placeholder="https://www.example.com"
              value={addUrl}
              onChange={(e) => {
                setAddUrl(e.target.value);
                if (addError) setAddError("");
              }}
              error={!!addError}
              fullWidth
            />
            <TextField
              size="small"
              label="Display name"
              placeholder="e.g. Aviation Herald"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Factual Reporting</InputLabel>
              <Select
                value={addLevel}
                label="Factual Reporting"
                onChange={(e) => setAddLevel(e.target.value)}
              >
                {FACTUAL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Manually added sources default to High. This overrides the Media Bias / Fact Check
              rating and is kept when ratings are reloaded.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <MDButton
              onClick={() => {
                setAddOpen(false);
                setAddError("");
              }}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              disabled={addBusy || !addUrl.trim()}
              sx={{ bgcolor: "#52009b", color: "#f7f4fb", "&:hover": { bgcolor: "#3a0070" } }}
              onClick={handleAdd}
            >
              {addBusy ? "Adding…" : "Add source"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit source — {editTarget?.domain}</DialogTitle>
          <DialogContent
            sx={{ pt: "16px !important", display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              size="small"
              label="Display name"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Factual Reporting</InputLabel>
              <Select
                value={editLevel}
                label="Factual Reporting"
                onChange={(e) => setEditLevel(e.target.value)}
              >
                {FACTUAL_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Editing the rating marks this source as a manual override, kept when ratings are
              reloaded.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <MDButton onClick={() => setEditTarget(null)}>Cancel</MDButton>
            <MDButton
              variant="contained"
              disabled={editBusy}
              sx={{ bgcolor: "#52009b", color: "#f7f4fb", "&:hover": { bgcolor: "#3a0070" } }}
              onClick={handleEditSave}
            >
              {editBusy ? "Saving…" : "Save"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* ID148 — block confirm */}
        <Dialog
          open={!!blockConfirm}
          onClose={() => !blockBusy && setBlockConfirm(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ color: "#b71c1c", fontWeight: 700 }}>
            ⚠ Add to Ignored Domains
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              Block <strong>{blockConfirm?.label}</strong>? Their domains will be added to the{" "}
              <strong>Ignored Domains</strong> list, so{" "}
              <strong>future ingest will skip them</strong>. Existing scan results are not changed.
              You can unblock a domain later from the Ignored Domains list.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <MDButton disabled={blockBusy} onClick={() => setBlockConfirm(null)}>
              Cancel
            </MDButton>
            <MDButton variant="contained" color="error" disabled={blockBusy} onClick={runBlock}>
              {blockBusy ? "Blocking…" : "Block domains"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Delete confirm */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: "#b71c1c", fontWeight: 700 }}>⚠ Remove source</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              Remove <strong>{deleteTarget?.title || deleteTarget?.domain}</strong> from the
              trusted-sources list? Future alerts from this domain will be marked{" "}
              <strong>Unranked</strong>. Existing alert ratings are not changed.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <MDButton onClick={() => setDeleteTarget(null)}>Cancel</MDButton>
            <MDButton
              variant="contained"
              color="error"
              disabled={deleteBusy}
              onClick={handleDelete}
            >
              {deleteBusy ? "Removing…" : "Remove"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
